import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/utils/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center transition-all duration-200 whitespace-nowrap rounded-md ring-offset-white focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-900 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "border border-amber-400 bg-amber-400 font-bold text-blue-950 shadow-sm hover:bg-amber-500 hover:border-amber-500",
                destructive: "bg-red-500 text-white shadow-sm hover:bg-red-600",
                outline:
                    "border border-blue-900/30 bg-white font-medium text-blue-950 shadow-sm hover:bg-amber-50 hover:border-blue-900/60",
                admin: "border border-blue-900 bg-blue-950 font-bold uppercase tracking-wide text-amber-100 shadow-md hover:bg-blue-900 hover:shadow-lg",
                secondary: "border border-blue-900/20 bg-blue-950 text-amber-100 shadow-md hover:bg-blue-900",
                ghost: "border-transparent font-special font-bold hover:bg-amber-400 hover:text-blue-950",
                link: "text-blue-950 underline-offset-4 hover:underline",
            },
            size: {
                default: "h-11 px-4 py-2.5 text-sm md:h-12 md:px-5 md:text-base",
                sm: "h-9 rounded-md px-3 text-sm md:h-10 md:px-4 md:text-base",
                lg: "h-12 rounded-md px-8 text-base md:h-13 md:px-10",
                icon: "h-11 w-11 md:h-12 md:w-12",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button"
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        )
    }
)
Button.displayName = "Button"

export { Button, buttonVariants }
